import type { AuthUser } from "../api/client";

/** Check whether the user holds a global scope. */
export function userHasGlobalScope(user: AuthUser | null | undefined, required: string): boolean {
  return !!user && user.scopes.includes(required);
}

/**
 * Check whether the user holds an organization-level scope for the given org.
 *
 * Global admins/developers (those with the `users:manage` global scope) bypass
 * the per-org check and are treated as having every org scope everywhere.
 */
export function userHasOrgScope(
  user: AuthUser | null | undefined,
  organizationId: string | null | undefined,
  required: string,
): boolean {
  if (!user || !organizationId) return false;
  if (userHasGlobalScope(user, "users:manage")) return true;
  const membership = user.organizations.find((o) => o.id === organizationId);
  return !!membership && membership.scopes.includes(required);
}

/**
 * Whether the user may manage an org's micro-app infrastructure (Google Sheets
 * connection + source registry, enable/publish controls).
 *
 * Mirrors the backend `assert_can_manage_org_apps`: a publisher/developer (they
 * carry the global `apps:write` scope) for any org, or an admin of this specific
 * org even without a global app role. A plain global admin is deliberately NOT
 * admitted here — unlike `userHasOrgScope`, this does not honour the
 * `users:manage` bypass, matching the backend rule that publishing stays off the
 * admin role.
 */
export function userCanManageOrgApps(
  user: AuthUser | null | undefined,
  organizationId: string | null | undefined,
): boolean {
  if (!user) return false;
  if (userHasGlobalScope(user, "apps:write")) return true;
  if (!organizationId) return false;
  const membership = user.organizations.find((o) => o.id === organizationId);
  return !!membership && membership.roles.includes("admin");
}

/**
 * Whether the user holds an organization-level scope in *any* of their orgs.
 *
 * Global admins/developers (`users:manage`) pass, as in `userHasOrgScope`. Use
 * this when the org is not at hand (e.g. prospect-list CRM status lookups).
 */
export function userHasOrgScopeAnywhere(
  user: AuthUser | null | undefined,
  required: string,
): boolean {
  if (!user) return false;
  if (userHasGlobalScope(user, "users:manage")) return true;
  return user.organizations.some((o) => o.scopes.includes(required));
}
